import {
  type ReactElement,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { type ZodType } from 'zod';

import {
  DEFAULT_REMOTE_CONFIG,
  RemoteConfigPeopleSearchedSchema,
  RemoteConfigPricingSchema,
  RemoteConfigRelatedQuestionsSchema,
  RemoteConfigSubscriptionCancelDelayMinutesSchema,
  type RemoteConfigType,
  RemoteConfigWebUpsellSchema,
  getRemoteConfigInstance,
} from '@/app/providers/firebase';

const RemoteConfigContext = createContext<RemoteConfigType | null>(null);

const safeJsonParse = (candidate: string): unknown => {
  try {
    return JSON.parse(candidate) as unknown;
  } catch {
    return candidate;
  }
};

const parseRemoteConfigField = <T,>({
  fieldName,
  schema,
  value,
  fallback,
}: {
  fieldName: keyof RemoteConfigType;
  schema: ZodType<T>;
  value: unknown;
  fallback: T;
}): T => {
  const parsed = schema.safeParse(value);

  if (parsed.success) {
    return parsed.data;
  }

  // eslint-disable-next-line no-console
  console.error(`Invalid Remote Config field: ${fieldName}`, parsed.error);

  return fallback;
};

const parseRemoteConfig = (
  structuredRemoteConfig: Partial<Record<keyof RemoteConfigType, unknown>>,
): RemoteConfigType => {
  return {
    webPeopleSearched: parseRemoteConfigField({
      fieldName: 'webPeopleSearched',
      schema: RemoteConfigPeopleSearchedSchema,
      value: structuredRemoteConfig.webPeopleSearched,
      fallback: DEFAULT_REMOTE_CONFIG.webPeopleSearched,
    }),
    webPeopleSearchedInside: parseRemoteConfigField({
      fieldName: 'webPeopleSearchedInside',
      schema: RemoteConfigPeopleSearchedSchema,
      value: structuredRemoteConfig.webPeopleSearchedInside,
      fallback: DEFAULT_REMOTE_CONFIG.webPeopleSearchedInside,
    }),
    webRelatedQuestions: parseRemoteConfigField({
      fieldName: 'webRelatedQuestions',
      schema: RemoteConfigRelatedQuestionsSchema,
      value: structuredRemoteConfig.webRelatedQuestions,
      fallback: DEFAULT_REMOTE_CONFIG.webRelatedQuestions,
    }),
    pricing: parseRemoteConfigField({
      fieldName: 'pricing',
      schema: RemoteConfigPricingSchema,
      value: structuredRemoteConfig.pricing,
      fallback: DEFAULT_REMOTE_CONFIG.pricing,
    }),
    webUpsell: parseRemoteConfigField({
      fieldName: 'webUpsell',
      schema: RemoteConfigWebUpsellSchema as ZodType<
        RemoteConfigType['webUpsell']
      >,
      value: structuredRemoteConfig.webUpsell,
      fallback: DEFAULT_REMOTE_CONFIG.webUpsell,
    }),
    subscriptionCancelDelayMinutes: parseRemoteConfigField({
      fieldName: 'subscriptionCancelDelayMinutes',
      schema: RemoteConfigSubscriptionCancelDelayMinutesSchema,
      value: structuredRemoteConfig.subscriptionCancelDelayMinutes,
      fallback: DEFAULT_REMOTE_CONFIG.subscriptionCancelDelayMinutes,
    }),
  };
};

export function RemoteConfigProvider({
  enabled = true,
  children,
}: {
  enabled?: boolean;
  children: ReactNode;
}): ReactElement {
  const [config, setConfig] = useState<RemoteConfigType | null>(
    enabled ? null : DEFAULT_REMOTE_CONFIG,
  );

  useEffect(() => {
    if (!enabled) {
      setConfig(DEFAULT_REMOTE_CONFIG);
      return;
    }

    const loadRemoteConfig = async (): Promise<void> => {
      try {
        const remoteConfig = await getRemoteConfigInstance();
        const { fetchAndActivate, getAll } =
          await import('firebase/remote-config');
        await fetchAndActivate(remoteConfig);
        const rawRemoteConfig = getAll(remoteConfig);

        const structuredRemoteConfig = Object.entries(rawRemoteConfig).reduce<
          Partial<Record<keyof RemoteConfigType, unknown>>
        >((config, [key, value]) => {
          return {
            ...config,
            [key as keyof RemoteConfigType]: safeJsonParse(value.asString()),
          };
        }, {});

        setConfig(parseRemoteConfig(structuredRemoteConfig));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        setConfig(DEFAULT_REMOTE_CONFIG);
      }
    };

    void loadRemoteConfig();
  }, [enabled]);

  return (
    <RemoteConfigContext.Provider value={config}>
      {children}
    </RemoteConfigContext.Provider>
  );
}

export function useRemoteConfig(): RemoteConfigType | null {
  return useContext(RemoteConfigContext);
}
